import React, { createContext, useContext, useState, useEffect } from 'react';

interface PromptFormData {
  id?: string;
  name: string;
  prompt: string;
  description: string;
  category_tags: string[];
  industry_tags: string[];
  type: 'image2image' | 'text2image' | 'image2video' | 'text2video' | 'morphing' | 'text2music' | 'text2speech';
  suggested_models?: string[];
}

interface CreativePromptContextType {
  isPromptDialogOpen: boolean;
  setIsPromptDialogOpen: (open: boolean) => void;
  formData: PromptFormData;
  setFormData: (data: PromptFormData) => void;
  resetForm: () => void;
  editMode: boolean;
  setEditMode: (edit: boolean) => void;
  openEditDialog: (prompt: any) => void;
  openCreateDialog: () => void;
}

const CreativePromptContext = createContext<CreativePromptContextType | undefined>(undefined);

const defaultFormData: PromptFormData = {
  name: '',
  prompt: '',
  description: '',
  category_tags: [],
  industry_tags: [],
  type: 'image2image',
  suggested_models: []
};

export const CreativePromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPromptDialogOpen, setIsPromptDialogOpen] = useState(false);
  const [formData, setFormData] = useState<PromptFormData>(defaultFormData);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+P (or Cmd+P on Mac) for "Prompt"
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        // Prevent default browser behavior (print dialog)
        event.preventDefault();
        
        // Only open if not already open and not in an input field
        const activeElement = document.activeElement;
        const isInputField = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           (activeElement as HTMLElement)?.contentEditable === 'true';
        
        if (!isInputField && !isPromptDialogOpen) {
          openCreateDialog();
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPromptDialogOpen]);

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditMode(false);
  };

  const openEditDialog = (prompt: any) => {
    setFormData({
      id: prompt.id,
      name: prompt.name || '',
      prompt: prompt.prompt || '',
      description: prompt.description || '',
      category_tags: prompt.category_tags || [],
      industry_tags: prompt.industry_tags || [],
      type: prompt.type || 'image2image',
      suggested_models: prompt.suggested_models || []
    });
    setEditMode(true);
    setIsPromptDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setIsPromptDialogOpen(true);
  };

  return (
    <CreativePromptContext.Provider value={{ 
      isPromptDialogOpen, 
      setIsPromptDialogOpen, 
      formData, 
      setFormData,
      resetForm,
      editMode,
      setEditMode,
      openEditDialog,
      openCreateDialog
    }}>
      {children}
    </CreativePromptContext.Provider>
  );
};

export const useCreativePrompt = () => {
  const context = useContext(CreativePromptContext);
  if (context === undefined) {
    throw new Error('useCreativePrompt must be used within a CreativePromptProvider');
  }
  return context;
};